import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, User } from 'lucide-react';
import api from '../api/client';

const HIDDEN_PREFIXES = [
  '/checkout',
  '/lives/', // active live detail (dashboard `/lives` stays)
  '/o2o/',
  '/session/',
  '/creator/onboarding',
  '/verify-email',
  '/paused',
];

const CLOSED_KEY = 'nymia_wa_widget_closed';

const isHiddenRoute = (pathname) =>
  !!pathname && HIDDEN_PREFIXES.some((p) => pathname.startsWith(p));

export const WhatsAppLogo = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.511-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.884 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.48-8.413Z" />
  </svg>
);

/**
 * WhatsApp support widget (Phase 70) — replaces the internal support chat.
 * Config (phone, welcome message, avatar) comes from the Admin panel via
 * GET /api/whatsapp-widget/config. Also opens on the legacy
 * `nymia:open-support-chat` event so the "Assistenza" menu entries keep working.
 * The small X badge hides the widget for the whole session (sessionStorage);
 * the `nymia:open-support-chat` event always re-opens it.
 */
export const WhatsAppWidget = () => {
  const location = useLocation();
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [closed, setClosed] = useState(() => {
    try { return sessionStorage.getItem(CLOSED_KEY) === '1'; } catch (_e) { return false; }
  });

  useEffect(() => {
    api.get('/whatsapp-widget/config').then(({ data }) => setCfg(data)).catch(() => setCfg(null));
  }, []);

  useEffect(() => {
    const handler = () => {
      try { sessionStorage.removeItem(CLOSED_KEY); } catch (_e) { /* ignore */ }
      setClosed(false);
      setOpen(true);
    };
    window.addEventListener('nymia:open-support-chat', handler);
    return () => window.removeEventListener('nymia:open-support-chat', handler);
  }, []);

  if (!cfg || !cfg.enabled || !cfg.phone || closed || isHiddenRoute(location.pathname)) return null;

  const dismissForSession = () => {
    try { sessionStorage.setItem(CLOSED_KEY, '1'); } catch (_e) { /* ignore */ }
    setOpen(false);
    setClosed(true);
  };

  const waUrl = `https://wa.me/${cfg.phone}${cfg.prefill_message ? `?text=${encodeURIComponent(cfg.prefill_message)}` : ''}`;

  return (
    <>
      {/* Welcome box — Dark & Gold */}
      {open && (
        <div
          className="fixed bottom-[8.5rem] right-3 z-[60] w-[320px] max-w-[calc(100vw-24px)] overflow-hidden rounded-2xl border border-amber-500/30 bg-neutral-950 shadow-2xl shadow-black/60 md:bottom-[5.5rem] md:right-4"
          data-testid="whatsapp-widget-box"
        >
          <div className="flex items-center gap-3 border-b border-amber-500/20 bg-gradient-to-r from-amber-500/[0.12] to-transparent px-4 py-3">
            <div className="relative">
              {cfg.avatar_url ? (
                <img
                  src={cfg.avatar_url}
                  alt="Nymia"
                  className="h-11 w-11 rounded-full border border-amber-500/40 object-cover"
                  data-testid="whatsapp-widget-avatar"
                />
              ) : (
                <div
                  className="flex h-11 w-11 items-center justify-center rounded-full border border-amber-500/40 bg-amber-500/10"
                  data-testid="whatsapp-widget-avatar-default"
                >
                  <User className="h-5 w-5 gold-text" strokeWidth={1.8} />
                </div>
              )}
              <span
                className={`absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-neutral-950 ${cfg.is_online ? 'bg-emerald-500' : 'bg-neutral-500'}`}
              />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-neutral-100">Nymia</p>
              <p
                className={`text-[11px] font-medium ${cfg.is_online ? 'text-emerald-400' : 'text-neutral-400'}`}
                data-testid="whatsapp-widget-status"
              >
                {cfg.is_online ? 'Online' : 'Offline'}
              </p>
              {cfg.hours_enabled && cfg.hours_label && (
                <p className="truncate text-[10px] text-neutral-500" data-testid="whatsapp-widget-hours">
                  {cfg.hours_label}
                </p>
              )}
            </div>
            <button
              onClick={() => setOpen(false)}
              className="rounded-full p-1.5 text-neutral-400 transition-colors hover:bg-white/10 hover:text-neutral-100"
              aria-label="Chiudi"
              data-testid="whatsapp-widget-close"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          <div className="px-4 py-4">
            <div
              className="relative max-w-[92%] rounded-2xl rounded-tl-sm border border-white/10 bg-neutral-900 px-3.5 py-2.5 text-[13px] leading-relaxed text-neutral-200"
              data-testid="whatsapp-widget-message"
            >
              {cfg.welcome_message}
            </div>
            {!cfg.is_online && cfg.offline_message && (
              <p className="mt-2 max-w-[92%] px-1 text-[11px] italic text-amber-300/80" data-testid="whatsapp-widget-offline-note">
                {cfg.offline_message}
              </p>
            )}
          </div>

          <div className="px-4 pb-4">
            <a
              href={waUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex h-10 w-full items-center justify-center gap-2 rounded-full bg-[#25D366] text-sm font-bold text-black transition-transform hover:scale-[1.02]"
              data-testid="whatsapp-widget-cta"
            >
              <WhatsAppLogo className="h-4 w-4" />
              Apri chat su WhatsApp
            </a>
          </div>
        </div>
      )}

      {/* Floating button — 46px, raised above the mobile bottom nav */}
      <div className="fixed bottom-20 right-3 z-[60] md:bottom-6 md:right-4">
        <button
          onClick={() => setOpen((o) => !o)}
          className="flex h-[46px] w-[46px] items-center justify-center rounded-full bg-[#25D366] text-white shadow-lg shadow-black/40 ring-2 ring-amber-500/40 transition-transform hover:scale-105"
          aria-label="Assistenza WhatsApp"
          data-testid="whatsapp-widget-fab"
        >
          <WhatsAppLogo className="h-6 w-6" />
        </button>
        <button
          onClick={dismissForSession}
          className="absolute -right-1.5 -top-1.5 flex h-5 w-5 items-center justify-center rounded-full border border-white/20 bg-neutral-900 text-neutral-300 shadow transition-colors hover:bg-neutral-800 hover:text-white"
          aria-label="Nascondi assistenza"
          data-testid="whatsapp-widget-dismiss"
        >
          <X className="h-3 w-3" />
        </button>
      </div>
    </>
  );
};

export default WhatsAppWidget;
